// Firestore access for rabbi profiles and per-tradition default rabbis.
// Collection "rabbiTraditionDefaults" docs: { rabbiId, readingPart: "parasha" | "haftara", tradition }

import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
  DocumentData,
} from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';
import { RabbiProfile } from '../pages/ChooseRabbiPage';
import { getSelectedRabbiId, getTradition } from './userSelections';

export type ReadingPart = 'parasha' | 'haftara';

export interface TraditionDefaultRabbis {
  parasha: RabbiProfile | null;
  haftara: RabbiProfile | null;
}

/** Map a "rabbies" Firestore document to the RabbiProfile shape used across the app. */
export function mapRabbiDoc(id: string, rabbi: DocumentData): RabbiProfile {
  // When recordedParashiot is not an array, null, or undefined → rabbi has all parashot
  const recordedParashiot = Array.isArray(rabbi.recordedParashiot)
    ? (rabbi.recordedParashiot as string[])
    : undefined;
  return {
    id: id || rabbi.key || '',
    initials: rabbi.key || id || '',
    key: rabbi.key,
    nameEn: rabbi.nameEn || rabbi.firstName || '',
    nameHe: rabbi.nameHe || rabbi.firstName || '',
    titleHe: rabbi.titleHe || '',
    phone: rabbi.phone || '',
    email:
      rabbi.email ||
      rabbi.emailAddress ||
      (rabbi as { mail?: string }).mail ||
      (rabbi as { contactEmail?: string }).contactEmail ||
      undefined,
    titleEn: rabbi.titleEn || '',
    rating: Number(rabbi.rating) || 0,
    reviews: Number(rabbi.reviews) || 0,
    tradition: (rabbi.nusach && rabbi.nusach.toLowerCase()) || rabbi.tradition || 'Sfarad',
    priceMonthly: Number(rabbi.priceMonthly) || 0,
    sampleUrl: rabbi.sampleUrl,
    imageUrl: rabbi.imageUrl,
    experienceYears: rabbi.experienceYears ? Number(rabbi.experienceYears) : undefined,
    studentCount: rabbi.studentCount ? Number(rabbi.studentCount) : undefined,
    roles: rabbi.roles || [],
    recordedParashiot,
  };
}

/**
 * Fetch the default rabbis configured for a tradition in "rabbiTraditionDefaults",
 * one per reading part (parasha / haftara). Missing or non-approved rabbis resolve to null.
 */
export async function fetchTraditionDefaultRabbis(
  tradition: string,
): Promise<TraditionDefaultRabbis> {
  const db = getFirestore(firebaseApp);
  // Fetch all defaults and filter case-insensitively to handle data inconsistencies
  // (some documents may be stored as 'Ashkenaz', others as 'ashkenaz').
  const snapshot = await getDocs(collection(db, 'rabbiTraditionDefaults'));
  const traditionLower = tradition.toLowerCase();

  const rabbiIdByPart: Partial<Record<ReadingPart, string>> = {};
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    const docTradition: unknown = data.tradition;
    if (typeof docTradition !== 'string' || docTradition.toLowerCase() !== traditionLower) return;
    const part: unknown = data.readingPart;
    const rabbiId: unknown = data.rabbiId;
    if (
      (part === 'parasha' || part === 'haftara') &&
      typeof rabbiId === 'string' &&
      rabbiId.trim() !== ''
    ) {
      rabbiIdByPart[part] = rabbiId.trim();
    }
  });

  const [parasha, haftara] = await Promise.all([
    rabbiIdByPart.parasha ? getRabbiById(rabbiIdByPart.parasha) : null,
    rabbiIdByPart.haftara ? getRabbiById(rabbiIdByPart.haftara) : null,
  ]);
  return { parasha, haftara };
}

// Rabbi details map: one fetch per rabbi id per session; failures are not cached.
const rabbiByIdCache = new Map<string, Promise<RabbiProfile | null>>();

/**
 * Rabbi details from the "rabbies" collection by document id, cached.
 * Non-approved rabbis resolve to null (no status means approved, for backward
 * compatibility).
 */
export function getRabbiById(rabbiId: string): Promise<RabbiProfile | null> {
  let pending = rabbiByIdCache.get(rabbiId);
  if (!pending) {
    const db = getFirestore(firebaseApp);
    pending = getDoc(doc(db, 'rabbies', rabbiId)).then((rabbiSnap) => {
      if (!rabbiSnap.exists()) return null;
      const data = rabbiSnap.data();
      const status = data.status as string | undefined;
      if (status != null && status !== 'approved') return null;
      return mapRabbiDoc(rabbiSnap.id, data);
    });
    pending.catch(() => rabbiByIdCache.delete(rabbiId));
    rabbiByIdCache.set(rabbiId, pending);
  }
  return pending;
}

/**
 * Look up a rabbi by connection code: the first 5 digits of their "rabbies"
 * document id. Uses a document-id range query (id >= code, id < code + '')
 * since Firestore has no native "starts with" operator. Returns the first
 * approved match, or null when no rabbi's id starts with the code.
 */
export async function findRabbiByCode(code: string): Promise<RabbiProfile | null> {
  const trimmed = code.trim();
  if (!trimmed) return null;
  const db = getFirestore(firebaseApp);
  const rabbiesRef = collection(db, 'rabbies');
  const rangeQuery = query(
    rabbiesRef,
    where(documentId(), '>=', trimmed),
    where(documentId(), '<', trimmed + String.fromCharCode(0xf8ff)),
  );
  const snapshot = await getDocs(rangeQuery);
  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const status = data.status as string | undefined;
    if (status != null && status !== 'approved') continue;
    return mapRabbiDoc(docSnap.id, data);
  }
  return null;
}

// One in-flight/resolved fetch per tradition per session; failures are not cached
// so a later call can retry.
const traditionDefaultsCache = new Map<string, Promise<TraditionDefaultRabbis>>();

/** Cached version of fetchTraditionDefaultRabbis. */
export function getTraditionDefaultRabbis(tradition: string): Promise<TraditionDefaultRabbis> {
  let pending = traditionDefaultsCache.get(tradition);
  if (!pending) {
    pending = fetchTraditionDefaultRabbis(tradition);
    pending.catch(() => traditionDefaultsCache.delete(tradition));
    traditionDefaultsCache.set(tradition, pending);
  }
  return pending;
}

/**
 * Rabbi for the persisted tradition (nusach) and reading part. Rabbi details are
 * never persisted: they are always resolved from the rabbiTraditionDefaults map
 * and the rabbies collection (cached). An explicit rabbi choice (ChooseRabbiPage,
 * stored as id only) that matches the tradition wins for parasha; haftara falls
 * back to the parasha default when no haftara record exists. Returns null while
 * loading or when nothing is configured for the tradition.
 */
export function useTraditionRabbi(part: ReadingPart = 'parasha'): RabbiProfile | null {
  const tradition = getTradition()?.key || 'ashkenaz';
  const selectedRabbiId = getSelectedRabbiId();
  const [defaults, setDefaults] = useState<TraditionDefaultRabbis | null>(null);
  const [selected, setSelected] = useState<RabbiProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTraditionDefaultRabbis(tradition)
      .then((d) => {
        if (!cancelled) setDefaults(d);
      })
      .catch((e) =>
        console.error(`Failed to load default rabbis for tradition "${tradition}":`, e),
      );
    return () => {
      cancelled = true;
    };
  }, [tradition]);

  useEffect(() => {
    if (!selectedRabbiId) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    getRabbiById(selectedRabbiId)
      .then((r) => {
        if (!cancelled) setSelected(r);
      })
      .catch((e) => console.error(`Failed to load rabbi "${selectedRabbiId}":`, e));
    return () => {
      cancelled = true;
    };
  }, [selectedRabbiId]);

  const selectedMatchesTradition =
    selected != null && selected.tradition?.toLowerCase() === tradition.toLowerCase();
  if (part === 'haftara') {
    return defaults?.haftara ?? defaults?.parasha ?? (selectedMatchesTradition ? selected : null);
  }
  return (selectedMatchesTradition ? selected : null) ?? defaults?.parasha ?? null;
}
